/**
 * Client records and their scope profiles.
 *
 * The scope profile is the text that will be injected into the AI classification prompt in
 * Phase 3, which is why it is a first-class editable field rather than a note: improving a
 * profile is how she improves the suggestions she gets.
 */

import { Fragment, useState } from "react";

import { api } from "../api";
import { formatDate, formatDateTime } from "../config";
import type { Client, FirmProfile } from "../types";
import { CLIENT_MONEY, missingForAssessment, SIZE_BANDS, STAGES } from "../types";

interface Props {
  clients: Client[];
  onChanged: () => void;
  onError: (error: unknown) => void;
}

export function ClientsView({ clients, onChanged, onError }: Props) {
  const [editing, setEditing] = useState<Client | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [frn, setFrn] = useState("");
  /**
   * Open a client's Register entry in a tab of its own.
   *
   * It used to render inside the card, and a firm's permissions and requirements are long
   * enough that the column made both them and the scope profile unreadable. A hash route
   * costs nothing to serve and carries her sign-in with it, so the tab is the cheap fix.
   */
  const lookup = (client: Client) => {
    window.open(`${window.location.origin}/#/clients/${client.id}`, "_blank", "noopener");
  };

  const create = async () => {
    try {
      await api.addClient({ name, website: website || undefined, frn: frn || undefined });
      setAdding(false);
      setName("");
      setWebsite("");
      setFrn("");
      onChanged();
    } catch (error) {
      onError(error);
    }
  };

  const save = async (client: Client, fields: Partial<Client>) => {
    try {
      await api.patchClient(client.id, fields);
      setEditing(null);
      onChanged();
    } catch (error) {
      onError(error);
    }
  };

  /**
   * Archived clients are kept, not deleted, and shown apart from the working list.
   *
   * Her log carries five years of tags and three of the names on it — Senguard, Alessia,
   * Morris — are clients Norwood no longer acts for. Their items stay tagged and their
   * records stay readable, because history that quietly loses its labels is worse than
   * history with dead names in it. But they are not work, so they do not sit in the list of
   * clients she is scoping and tagging against, where six cards imply six engagements.
   */
  const current = clients.filter((client) => client.status !== "archived");
  const archived = clients.filter((client) => client.status === "archived");

  const card = (client: Client) => (
    <ClientCard
      key={client.id}
      client={client}
      editing={editing?.id === client.id}
      onLookup={() => lookup(client)}
      onToggleEdit={() => setEditing(editing?.id === client.id ? null : client)}
      onSave={(fields) => save(client, fields)}
      onStatusChange={(status) => save(client, { status })}
    />
  );

  return (
    <div className="clients">
      <div className="toolbar">
        <strong>{current.length} client{current.length === 1 ? "" : "s"}</strong>
        <span className="spacer" />
        <button className="primary" onClick={() => setAdding(true)}>
          Add client
        </button>
      </div>

      {adding && (
        <div className="card">
          <h3>New client</h3>
          <label>
            Name
            <input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
          </label>
          <label>
            Website
            <input
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
              placeholder="https://…"
            />
          </label>
          <label>
            FCA firm reference number
            <input value={frn} onChange={(event) => setFrn(event.target.value)} placeholder="optional" />
          </label>
          <div className="row">
            <button className="primary" disabled={!name.trim()} onClick={create}>
              Create
            </button>
            <button onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {!clients.length && !adding && (
        <div className="empty">
          <p className="muted">
            No client records yet. Her log already carries the tags — Faciit, Finspector,
            Senguard, Alessia, Morris — so adding them here is what turns a tag into a record
            with a scope profile.
          </p>
        </div>
      )}

      {!current.length && archived.length > 0 && !adding && (
        <div className="empty">
          <p className="muted">
            Every client record is archived. The {archived.length} below are kept for the
            history their tags carry; add a client to start scoping again.
          </p>
        </div>
      )}

      <ul className="cards">{current.map(card)}</ul>

      {archived.length > 0 && (
        <details className="archive">
          <summary>
            Archive <span className="pill archived">{archived.length}</span>
            <span className="muted small">
              {" "}
              — former clients. Their items stay tagged and their records stay readable.
            </span>
          </summary>
          <ul className="cards">{archived.map(card)}</ul>
        </details>
      )}
    </div>
  );
}

function ClientCard({
  client,
  editing,
  onLookup,
  onToggleEdit,
  onSave,
  onStatusChange,
}: {
  client: Client;
  editing: boolean;
  onLookup: () => void;
  onToggleEdit: () => void;
  onSave: (fields: Partial<Client>) => void;
  onStatusChange: (status: Client["status"]) => void;
}) {
  const archived = client.status === "archived";
  const [confirming, setConfirming] = useState(false);

  return (
    <li className="card">
      <div className="row">
        <h3>{client.name}</h3>
        <span className={`pill ${client.status}`}>{client.status}</span>
        <span className="spacer" />
        <button
          className="link"
          disabled={!client.frn}
          title={
            client.frn
              ? `Open FRN ${client.frn} on the FCA Register, in a new tab`
              : /* Two reasons for the same disabled button, and telling them apart matters:
                   one is a client working towards authorisation, whose entry will exist in a
                   few months, and the other is a firm that will never have one. */
                client.firm_profile?.stage === "pre-authorisation"
                ? "Not authorised yet, so there is no Register entry to read. There will be."
                : client.firm_profile?.stage === "not seeking authorisation"
                  ? "Not an authorised firm, so the Register has nothing on them"
                  : "No firm reference number recorded, so there is nothing to look up"
          }
          onClick={onLookup}
        >
          FCA lookup ↗
        </button>
        {archived ? (
          /* Back as a draft, not straight to active: a client she stopped acting for and
             has taken on again has a scope profile written for the old engagement, and
             that profile is about to start driving their tagging again. Draft is the
             prompt to read it first. */
          <button
            className="link"
            title="Make this client live again, as a draft, so the scope profile gets a look before it drives tagging"
            onClick={() => onStatusChange("draft")}
          >
            restore
          </button>
        ) : (
          <button
            className="link"
            title="Stop acting for this client. Nothing is deleted."
            onClick={() => setConfirming(true)}
          >
            archive
          </button>
        )}
        <button className="link" onClick={onToggleEdit}>
          {editing ? "close" : "edit"}
        </button>
      </div>

      {/* Spelled out rather than a bare "are you sure?", because the fear the word
          "archive" creates is that five years of tagged history is about to go, and the
          honest answer is that none of it moves. */}
      {confirming && (
        <div className="confirm">
          <p className="confirm-lede">
            Archive {client.name}? <strong>Nothing is deleted.</strong>
          </p>
          <ul className="confirm-list">
            <li>
              The record stays, and so does its scope profile — you can read it whenever you
              like.
            </li>
            <li>
              Every item tagged {client.name} keeps that tag. The log reads exactly as it
              does today, and always will.
            </li>
            <li>
              What changes: {client.name} moves to the Archive at the foot of this page, drops
              out of client tagging, and new items stop being judged against them.
            </li>
            <li>Reversible at any time — archived clients have a “restore” button.</li>
          </ul>
          <div className="row">
            <button
              className="primary"
              onClick={() => {
                setConfirming(false);
                onStatusChange("archived");
              }}
            >
              Archive {client.name}
            </button>
            <button onClick={() => setConfirming(false)}>Cancel</button>
          </div>
        </div>
      )}
      {client.website && (
        <a href={client.website} target="_blank" rel="noreferrer noopener">
          {client.website}
        </a>
      )}
      {client.frn && <p className="muted small">FRN {client.frn}</p>}
      {/* The one-line summary: what this client is, before the full profile. */}
      {client.notes && <p className="summary">{client.notes}</p>}
      {client.scope_profile && !editing && (
        <details className="scope-details">
          <summary>Scope profile</summary>
          <pre className="scope">{client.scope_profile}</pre>
        </details>
      )}
      {!editing && <FirmProfileSummary client={client} />}
      {!editing && <HandbookScopeSummary client={client} />}
      {client.updated_at && (
        <p className="muted small">
          Last changed {formatDateTime(client.updated_at)} by {client.updated_by}
        </p>
      )}

      {editing && <ClientEditor client={client} onSave={onSave} />}
    </li>
  );
}

/**
 * The firm profile, read-only, folded away — and loud when it is missing.
 *
 * Loud because of a rule the impact assessments run on: **no firm profile, no assessment.**
 * A document whose scope block is guesswork is worse than no document, so the generator
 * refuses rather than inventing. That refusal has to be visible here, on the client, months
 * before anybody clicks a button and wonders why this client produced nothing.
 */
function FirmProfileSummary({ client }: { client: Client }) {
  const profile = client.firm_profile;
  const missing = missingForAssessment(profile);
  const rows: [string, string][] = profile
    ? (
        [
          ["Business", profile.business_model],
          ["Where", (profile.jurisdictions || []).join(", ")],
          ["Size", profile.size_band],
          ["Stage", profile.stage],
          ["Authorised", profile.authorised_on ? formatDate(profile.authorised_on) : ""],
          ["Client money", profile.client_money],
          ["Volumes", profile.volumes],
          ["Applying for", (profile.permissions_sought || []).join(", ")],
          ["Not permitted", (profile.permissions_not_held || []).join(", ")],
          ["Excludes", (profile.exclusions || []).join(", ")],
          ["On the roadmap", (profile.roadmap || []).join(", ")],
          ["Reports to", (profile.prepared_for || []).join(", ")],
          ["Action owners", (profile.owner_titles || []).join(", ")],
          ["Proportionality", profile.proportionality_anchor],
        ] as [string, string | undefined][]
      )
        .filter((entry): entry is [string, string] => Boolean(entry[1]))
    : [];

  if (!rows.length) {
    return (
      <p className="small missing-profile">
        No firm profile.{" "}
        <span className="muted">
          Impact assessments cannot be written for this client until there is one — the scope
          block would be guesswork.
        </span>
      </p>
    );
  }

  return (
    <details className="scope-details">
      <summary>
        Firm profile
        {missing.length > 0 && (
          <span className="pill draft" title={`Still needed: ${missing.join(", ")}`}>
            incomplete
          </span>
        )}
      </summary>
      <dl className="firm-facts">
        {rows.map(([label, value]) => (
          <Fragment key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </Fragment>
        ))}
      </dl>
      {missing.length > 0 && (
        <p className="small muted">
          Not enough for an assessment yet — still needs {missing.join(", ")}.
        </p>
      )}
      <p className="small muted">
        Permissions are not kept here. They are read live from the FCA Register, because a
        cached authorisation is a lapsed authorisation shown without a warning.
      </p>
    </details>
  );
}

/**
 * Which parts of the rulebook this client cares about — read-only, and deliberately so.
 *
 * A reference only becomes an entity id by being resolved against a real Handbook tree, and
 * the tree is a megabyte of JSON in S3 that the API has no business loading per request. So
 * resolution is a batch job (`make handbook-scope`) and this shows its result. Letting her type
 * an entity id straight in here would be handing her the one input the security rule exists to
 * refuse — the point is that nothing reaches a request without having been found in the tree.
 */
function HandbookScopeSummary({ client }: { client: Client }) {
  const scope = client.handbook_scope || [];
  if (!scope.length) return null;

  const widest = scope.filter((entry) => entry.type === "sourcebook" || entry.type === "blocks");
  return (
    <details className="scope-details">
      <summary>
        Rulebook scope <span className="chip">{scope.length}</span>
      </summary>
      <ul className="handbook-scope">
        {scope.map((entry) => (
          <li key={entry.entity_id}>
            <code>{entry.reference}</code>
            <span className="muted small"> {entry.name}</span>
            {entry.tier && <span className="pill draft">tier {entry.tier}</span>}
          </li>
        ))}
      </ul>
      <p className="small muted">
        {widest.length > 0 && (
          <>
            {widest.length} whole sourcebook{widest.length === 1 ? "" : "s"} — every chapter in
            them is watched.{" "}
          </>
        )}
        Resolved against the Handbook tree, so each line is a part of the rulebook that really
        exists. Re-run the resolver after a Handbook change; a reference that stops existing is
        reported rather than dropped.
      </p>
    </details>
  );
}

/** A comma-separated input for a list field. Simplest thing that lets her edit one. */
function ListInput({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string[] | undefined;
  placeholder?: string;
  onChange: (next: string[]) => void;
}) {
  return (
    <label>
      {label}
      <input
        value={(value || []).join(", ")}
        placeholder={placeholder}
        onChange={(event) =>
          onChange(
            event.target.value
              .split(",")
              .map((part) => part.trim())
              .filter(Boolean),
          )
        }
      />
    </label>
  );
}

function FirmProfileEditor({
  profile,
  onChange,
}: {
  profile: FirmProfile;
  onChange: (next: FirmProfile) => void;
}) {
  const set = (changes: Partial<FirmProfile>) => onChange({ ...profile, ...changes });
  // Pre-authorisation is a first-class state, not a gap — getting the unregulated ones
  // regulated is the engagement — so the form asks a different question of them.
  const preAuth = profile.stage === "pre-authorisation";

  return (
    <fieldset className="firm-editor">
      <legend>Firm profile</legend>
      <p className="small muted">
        What kind of firm this is — not what controls they have. An impact assessment is
        calibrated against these facts, and the negatives do as much work as the positives:
        “no client money permission” is what lets an assessment rule provisions out in writing.
      </p>

      <label>
        Business model
        <input
          value={profile.business_model || ""}
          placeholder="credit broker"
          onChange={(event) => set({ business_model: event.target.value })}
        />
      </label>
      <ListInput
        label="Jurisdictions"
        value={profile.jurisdictions}
        placeholder="UK only"
        onChange={(jurisdictions) => set({ jurisdictions })}
      />
      <label>
        Size
        <select value={profile.size_band || ""} onChange={(e) => set({ size_band: e.target.value })}>
          {SIZE_BANDS.map((band) => (
            <option key={band} value={band}>
              {band || "—"}
            </option>
          ))}
        </select>
      </label>
      <label>
        Stage
        <select value={profile.stage || ""} onChange={(e) => set({ stage: e.target.value })}>
          {STAGES.map((stage) => (
            <option key={stage} value={stage}>
              {stage || "—"}
            </option>
          ))}
        </select>
      </label>
      {preAuth ? (
        <ListInput
          label="Applying for"
          value={profile.permissions_sought}
          placeholder="credit broking"
          onChange={(permissions_sought) => set({ permissions_sought })}
        />
      ) : (
        <label>
          Authorised on
          <input
            type="date"
            value={profile.authorised_on || ""}
            onChange={(event) => set({ authorised_on: event.target.value })}
          />
          <span className="small muted">
            Most target dates in an assessment count from this.
          </span>
        </label>
      )}
      {preAuth && (
        <p className="small muted">
          Nothing to look up on the FCA Register until they are through, and no authorisation
          date yet — which is why an assessment for them reads “you will need to be able to
          evidence this at application” rather than “you must have this”. What they are applying
          for is what scopes it.
        </p>
      )}
      <label>
        Holds client money
        <select
          value={profile.client_money || ""}
          onChange={(event) => set({ client_money: event.target.value })}
        >
          {CLIENT_MONEY.map((option) => (
            <option key={option} value={option}>
              {option || "— not said"}
            </option>
          ))}
        </select>
        <span className="small muted">
          “No” is an assertion an assessment leans on. Leaving it unset means nobody has said,
          which is a different thing.
        </span>
      </label>
      <label>
        Volumes
        <input
          value={profile.volumes || ""}
          placeholder="low transaction volume and value"
          onChange={(event) => set({ volumes: event.target.value })}
        />
      </label>
      <ListInput
        label="Relevant permissions NOT held"
        value={profile.permissions_not_held}
        placeholder="client money, credit lending"
        onChange={(permissions_not_held) => set({ permissions_not_held })}
      />
      <ListInput
        label="Exclusions"
        value={profile.exclusions}
        placeholder="not a TCSP, not a cryptoasset business"
        onChange={(exclusions) => set({ exclusions })}
      />
      <ListInput
        label="On the roadmap"
        value={profile.roadmap}
        placeholder="client money, insurance distribution"
        onChange={(roadmap) => set({ roadmap })}
      />
      <span className="small muted">
        What they intend to start doing. This is the one entry here that makes material{" "}
        <em>more</em> relevant, not less — a firm about to hold client money should be reading
        the client money rules now. It never becomes a permission, and it never overrides what
        the Register says they can do today.
      </span>
      <ListInput
        label="Assessments addressed to"
        value={profile.prepared_for}
        placeholder="SMF, MLRO"
        onChange={(prepared_for) => set({ prepared_for })}
      />
      <ListInput
        label="Action owners"
        value={profile.owner_titles}
        placeholder="MLRO, Compliance"
        onChange={(owner_titles) => set({ owner_titles })}
      />
      <span className="small muted">
        Titles, not names. In a small firm they collapse — one person is often both MLRO and
        Compliance Oversight, and a plan listing six owners describes a firm that does not
        exist.
      </span>
      <label>
        Proportionality anchor
        <input
          value={profile.proportionality_anchor || ""}
          placeholder="MLR 2017 Reg.4 / FCG 2"
          onChange={(event) => set({ proportionality_anchor: event.target.value })}
        />
      </label>
    </fieldset>
  );
}

function ClientEditor({
  client,
  onSave,
}: {
  client: Client;
  onSave: (fields: Partial<Client>) => void;
}) {
  const [status, setStatus] = useState(client.status);
  const [website, setWebsite] = useState(client.website ?? "");
  const [frn, setFrn] = useState(client.frn ?? "");
  const [scope, setScope] = useState(client.scope_profile ?? "");
  const [profile, setProfile] = useState<FirmProfile>(client.firm_profile ?? {});

  return (
    <div className="editor">
      <label>
        Status
        <select value={status} onChange={(event) => setStatus(event.target.value as Client["status"])}>
          <option value="draft">draft</option>
          <option value="active">active</option>
          <option value="archived">archived</option>
        </select>
      </label>
      <label>
        Website
        <input value={website} onChange={(event) => setWebsite(event.target.value)} />
      </label>
      <label>
        FRN
        <input value={frn} onChange={(event) => setFrn(event.target.value)} />
      </label>
      <label>
        Scope profile
        <textarea
          rows={10}
          value={scope}
          onChange={(event) => setScope(event.target.value)}
          placeholder="Which sectors, regulators and Handbook areas this client cares about. This text is what the AI classification will be told about them."
        />
      </label>
      <FirmProfileEditor profile={profile} onChange={setProfile} />
      <button
        className="primary"
        onClick={() =>
          onSave({ status, website, frn, scope_profile: scope, firm_profile: profile })
        }
      >
        Save
      </button>
    </div>
  );
}
